import axios from 'axios';
import { mapState } from 'vuex';
import { handleAPIError, ERROR_TYPE } from '@/utils/errorHelper';
import PageHeader from '@/components/widgets/PageHeader/PageHeader.vue';
import Loading from '@/components/widgets/Loading/Loading.vue';
import PasswordInput from '@/components/PasswordInput.vue';

// @vue/component
export default {
  name: 'AcceptInvite',
  components: {
    PageHeader,
    Loading,
    PasswordInput,
  },
  data() {
    return {
      product: {
        name: 'Teamwork.com',
        code: '',
        url: '',
        helpDocsLink: 'https://www.teamwork.com/support',
      },

      user: {
        id: 0,
        userId: '',
        firstName: '',
        lastName: '',
        chatHandle: '',
        password: '',
      },

      expired: false,
      formError: '',
      isLoading: true,
      nameIsSetInProjects: false,
    };
  },
  created() {
    this.code = this.$route.query.code;
    this.product.code = this.$route.params.productCode ? this.$route.params.productCode : 'chat';
    this.getValidateInviteCode();
  },
  computed: {
    ...mapState({
      siteInfo: (state) => state.site.siteInfo,
    }),
    title() {
      if (this.expired) return 'Invalid Code';
      if (!this.siteInfo.displayOptions.showSiteNameOnLoginScreen) {
        if (this.nameIsSetInProjects) return `Welcome ${this.user.firstName}!`;
        return 'Welcome!';
      } else if (!this.nameIsSetInProjects) return `Hi, welcome to ${this.siteInfo.name}!`;
      return `Hi ${this.user.firstName}, welcome to ${this.siteInfo.name}!`;
    },
    canSubmit() {
      const isValid = this.user && this.user.firstName.length &&
        this.user.lastName.length && this.user.password.length;

      if (this.isProductChat) {
        return isValid && this.user.chatHandle.length;
      }

      return isValid;
    },
    isProductChat() {
      return this.product.code === 'chat';
    },
    isProductProjects() {
      return this.product.code === 'projects';
    },
    showNameInputs() {
      return !this.isProductProjects || !this.nameIsSetInProjects;
    },
    subTitle() {
      if (this.isProductProjects && !this.showNameInputs) {
        return 'Please choose a password to get started.';
      }
      return 'Fill out your details below to get started.';
    },
    loginLink() {
      let url = '/login';
      if (this.$route.params.productCode) url += `/${this.$route.params.productCode}`;
      return url;
    },
  },
  methods: {
    onSubmit() {
      if (!this.canSubmit || this.isLoading) return;
      this.isLoading = true;

      const data = {
        firstName: this.user.firstName,
        lastName: this.user.lastName,
        password: this.user.password,
      };

      let url = `/launchpad/v1/join.json?code=${this.code}`;

      if (this.isProductChat) data.userHandle = this.user.chatHandle;
      else if (this.isProductProjects) {
        data.code = this.code;
        data.userId = this.user.userId;
        url = '/acceptinviteprojects.json';
      }

      axios.post(url, data).then((response) => {
        if (this.isProductProjects && response.data.error) {
          this.formError = response.data.message;
          this.isLoading = false;
        } else {
          window.location.href = response.data.redirectURL;
        }
      }).catch((e) => {
        const apiError = handleAPIError(this.$store, e, 'An error has occurred');
        if (apiError && apiError.type === ERROR_TYPE.FORM_ERROR && (apiError.err === 'User not invited' || apiError.err === 'User not found')) {
          this.$store.commit('showFatalError', {
            error: 'Invalid Invite Code',
            message: 'The code may have expired, please request another invitation.',
          });
        } else if (apiError && apiError.type === ERROR_TYPE.FORM_ERROR) {
          this.formError = apiError.err;
        }
        this.isLoading = false;
      });
    },
    getValidateInviteCode() {
      let url = `/launchpad/v1/join.json?code=${this.code}`;
      if (this.isProductProjects) {
        url = `/validateuserinvitecode.json?code=${this.code}`;
      }

      axios.get(url).then((response) => {
        if (response.data.error) {
          const err = handleAPIError(this.$store, { response }, 'Invalid invite code.');
          if (err.type === ERROR_TYPE.FORM_ERROR) {
            this.expired = true;
          } else {
            this.$store.commit('showFatalError', {
              error: response.data.error,
              message: response.data.message,
            });
          }

          this.isLoading = false;
        }

        if (!this.isProductProjects) {
          // Save product info
          this.product.name = response.data.productName;
          this.product.code = response.data.productCode;
          this.product.url = response.data.productUrl;
          this.product.helpDocsLink = response.data.productHelpDocsLink;
        }

        // not yet accepted, show form to update details
        if (response.data.userId) {
          this.user.id = response.data.userId;
          this.user.firstName = response.data.userFirstName;
          this.user.lastName = response.data.userLastName;
          this.user.chatHandle = response.data.userChatHandle;

          // if invited without name, clear out placeholders
          if (this.user.firstName === 'Unknown') {
            this.user.firstName = '';
            this.user.chatHandle = '';
          }
          if (this.user.lastName === 'Unknown') {
            this.user.lastName = '';
            this.user.chatHandle = '';
          }
        } else if (response.data.userLogin) {
          this.nameIsSetInProjects = response.data.nameIsSet;
          this.user.userId = response.data.userLogin;
          if (this.nameIsSetInProjects) {
            this.user.firstName = response.data.firstName;
            this.user.lastName = response.data.lastName;
          }
        }

        this.isLoading = false;
      }).catch((e) => {
        const err = handleAPIError(this.$store, e, 'Invalid invite code.');
        // most likely, the invite code has expired, show fatal anyway
        if (err) {
          if (err.type === ERROR_TYPE.FORM_ERROR) {
            this.expired = true;
          } else {
            this.$store.commit('showFatalError', {
              error: 'Invalid Invite Code',
              message: 'The code may have expired or has already been used, please request another invitation or try to log in.',
            });
          }
        }

        this.isLoading = false;
      });
    },
    reload() {
      window.location.reload();
    },
  },
};



// WEBPACK FOOTER //
// ./src/components/pages/AcceptInvite/AcceptInvite.js